import { isFeatureOn } from 'featureToggles';
import { AdditionalIncomeSectionHeading } from 'views/employment/AdditionalIncomeSectionHeading';
import {
  yesNoOptions,
  industryOptionsNZ,
  occupationOptions,
  additionalIncomeTypeGroupsNZ,
  numbersOnlyPattern,
  isRentalOrInvestment,
  isSelfEmployedByStatus,
  isEligibleForBankConnectAndBankStatementNz,
  isSuperOrGov,
  employmentStatusOptionsNzV2,
  UNEMPLOYED,
  unemploymentStatusOptionsNz,
  SUPERANNUATION,
  GOVERNMENT_BENEFIT,
} from '../_constants';
import { validEmployerNamePattern } from './constants';

const incomeValidationMessages = {
  required: 'Please enter your take-home pay.',
  frequencyRequired: 'Please select a frequency.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
  invalid: 'Please enter a whole dollar amount, without cents.',
};

const additionalIncomeValidationMessages = {
  required: 'Please enter your additional income amount.',
  frequencyRequired: 'Please select a frequency.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
  invalid: 'Please enter a whole dollar amount, without cents.',
};

const lengthOfTimeAtCurrentEmployerMessages = {
  required: 'Please enter your length of time employed.',
  invalid: 'Please enter numbers only.',
  monthsError: 'Please enter the number of months at your current employer. If less than 1 month, enter 0.',
  yearsError: 'Please enter your length of time employed.',
  monthRangeError: 'Please provide a number between {{minMonths}}-{{maxMonths}}.',
  yearRangeError: 'Please provide a number between {{minYears}}-{{maxYears}}.',
  excessAgeError: 'The length of time looks too high. Please review your input.',
};

const employerNameMessages = {
  required: 'Please provide us the name of the company you work at.',
  invalid: `Company name contains an invalid character. Must only contain letters, numbers and these characters: ( & , - . : ; ' " ! @ # $ % ^ & * () _ + = ? /.' )`,
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
};

const validationMessages = {
  income: incomeValidationMessages,
  lengthOfTimeAtCurrentEmployer: lengthOfTimeAtCurrentEmployerMessages,
  employerName: employerNameMessages,
};
const selfEmployedEmployerNameMessages = {
  required: 'Please enter your business name.',
  invalid: 'Please enter alphanumeric characters only.',
  minLength: 'Must have at least {{minLength}} characters.',
  maxLength: 'Must have at most {{maxLength}} characters',
};

const hasEmployerByOccupation = values => {
  const { occupation } = values;
  const hasNoEmployer = ['UNEMPLOYED', 'STUDENT', 'SELF_EMPLOYED', 'RETIRED', 'BENEFIT'].includes(occupation);

  return !hasNoEmployer;
};

const getYearlyIncome = income => {
  if (!income) {
    return 0;
  }

  const { amount, frequency } = income;
  switch (frequency) {
    case 'quarterly':
      return amount * 4;
    case 'monthly':
      return amount * 12;
    case 'fortnightly':
      return amount * 26;
    case 'weekly':
    default:
      return amount * 52;
  }
};

const getTotalYearlyIncome = data => {
  const income = getYearlyIncome(data.income);
  const otherIncome = getYearlyIncome(data.otherIncome);

  return income + otherIncome;
};

export const validateExpectedIncomeChangeConfig = () => {
  let initialIncome;

  return {
    onMount: values => {
      initialIncome = getTotalYearlyIncome(values);
    },
    onUnmount: () => {
      initialIncome = undefined;
    },
    onValidate: values => {
      const currentIncome = getTotalYearlyIncome(values);
      const offset = currentIncome - initialIncome;
      return offset < 0;
    },
    onValidationChange: (isValid, formData) => {
      if (isValid) {
        formData.setValues(values => ({
          ...values,
          expectedIncomeChange: 'no',
        }));
      }
    },
  };
};

export const employmentDetails = [
  {
    component: 'Select',
    name: 'employmentStatus',
    label: 'Primary income type',
    options: employmentStatusOptionsNzV2,
    testId: 'employment-status',
    validate: {
      messages: {
        required: 'Please provide us your employment status.',
      },
    },
  },
  {
    condition: values => !!values.employmentStatus,
    name: 'hasEmploymentStatus',
    fields: [
      {
        condition: values => UNEMPLOYED === values.employmentStatus,
        component: 'Select',
        name: 'incomeSource',
        label: 'What best describes your situation?',
        testId: 'income-source',
        options: unemploymentStatusOptionsNz,
        validate: {
          messages: {
            required: 'Please select an option that best describes your situation',
          },
        },
      },
      {
        condition: values =>
          UNEMPLOYED !== values.employmentStatus &&
          !isSuperOrGov(values.employmentStatus) &&
          !isRentalOrInvestment(values.employmentStatus),
        name: 'hasOccupation',
        fields: [
          {
            component: 'Select',
            name: 'occupation',
            label: 'Occupation',
            options: occupationOptions,
            testId: 'occupation',
            validate: {
              messages: {
                required: 'Please select your occupation.',
              },
            },
          },
          {
            condition: values =>
              UNEMPLOYED !== values.employmentStatus && !isRentalOrInvestment(values.employmentStatus),
            component: 'Select',
            name: 'industry',
            label: 'Employment industry',
            options: industryOptionsNZ,
            testId: 'industry',
            validate: {
              messages: {
                required: 'Please select your industry.',
              },
            },
          },
          {
            condition: values =>
              hasEmployerByOccupation(values) &&
              !isSelfEmployedByStatus(values.employmentStatus) &&
              !isRentalOrInvestment(values.employmentStatus),
            component: 'Input',
            name: 'employerName',
            label: 'Company name',
            testId: 'employer-name',
            validate: {
              props: {
                minLength: 1,
                maxLength: 30,
                validPattern: validEmployerNamePattern,
              },
              messages: validationMessages.employerName,
            },
          },
          {
            condition: values =>
              UNEMPLOYED !== values.employmentStatus &&
              isSelfEmployedByStatus(values.employmentStatus) &&
              !isRentalOrInvestment(values.employmentStatus),
            component: 'Input',
            name: 'employerName',
            label: 'Company name',
            testId: 'employer-name',
            validate: {
              props: {
                minLength: 1,
                maxLength: 30,
                validPattern: validEmployerNamePattern,
              },
              messages: selfEmployedEmployerNameMessages,
            },
          },
        ],
      },
      {
        component: 'TimeSpan',
        name: 'lengthOfTimeAtCurrentEmployer',
        label: values => {
          if ([SUPERANNUATION].includes(values.incomeSource) && [UNEMPLOYED].includes(values.employmentStatus)) {
            return `How long have you been receiving this Superannuation?`;
          }
          if ([GOVERNMENT_BENEFIT].includes(values.incomeSource) && [UNEMPLOYED].includes(values.employmentStatus)) {
            return `How long have you been receiving this Government benefit?`;
          }
          if ([UNEMPLOYED].includes(values.employmentStatus)) {
            return `How long have you been receiving this income?`;
          }
          return 'Length of time at current employer';
        },
        testId: 'current-employer-length',
        validate: {
          props: { minYears: 0, maxYears: 99, minMonths: 0, maxMonths: 11, validPattern: numbersOnlyPattern },
          messages: validationMessages.lengthOfTimeAtCurrentEmployer,
          component: 'TimeSpan',
        },
      },
      {
        condition: values =>
          !isFeatureOn('incomeReplay') || !isEligibleForBankConnectAndBankStatementNz(values.employmentStatus),
        component: 'Frequency',
        name: 'income',
        label: 'Your take-home pay?',
        helpText: 'The amount you receive after tax and deductions',
        mode: 'select',
        amountPrefix: '$',
        selectPlaceholder: 'Frequency',
        annually: false,
        testId: 'income',
        validate: {
          props: { minLength: 1, maxLength: 6, validPattern: numbersOnlyPattern },
          messages: validationMessages.income,
          component: 'Frequency',
        },
      },
      {
        component: 'RadioTile',
        name: 'hasOtherIncome',
        label: 'Do you have additional income?',
        helpText: 'e.g. rental income, government benefits, gig work (Uber, DiDi)',
        testId: 'has-other-income',
        options: yesNoOptions,
        showCircles: true,
        validate: {
          messages: {
            required: 'Please select yes or no.',
          },
        },
      },
      {
        condition: values => values.hasOtherIncome === 'yes',
        name: 'otherIncomeGroup',
        fields: [
          {
            component: 'CustomComponent',
            name: 'additionalIncomeSectionHeading',
            children: AdditionalIncomeSectionHeading,
          },
          {
            component: 'GroupedSelect',
            name: 'otherEmploymentStatus',
            label: 'Additional income type',
            placeholder: 'Please select',
            groups: additionalIncomeTypeGroupsNZ,
            testId: 'other-employment-status',
            validate: {
              messages: {
                required: 'Please select an additional income type.',
              },
            },
          },
          {
            condition: values =>
              !isFeatureOn('incomeReplay') || !isEligibleForBankConnectAndBankStatementNz(values.employmentStatus),
            component: 'Frequency',
            name: 'otherIncome',
            label: 'Additional income amount',
            helpText: 'The amount you receive',
            mode: 'select',
            amountPrefix: '$',
            selectPlaceholder: 'Frequency',
            annually: false,
            testId: 'other-income',
            validate: {
              props: { minLength: 1, maxLength: 6, validPattern: numbersOnlyPattern },
              messages: additionalIncomeValidationMessages,
              component: 'Frequency',
            },
          },
        ],
      },
    ],
  },
];
